import React, { useState, useMemo, useContext } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ScatterChart, Scatter, ZAxis, RadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import {
  TrendingUp, Star, DollarSign,
  Sparkles, Eye, Loader2, Target, RefreshCw, Award,
  Building2,
  Info
} from 'lucide-react';
import FormulaReveal, { HOTEL_FORMULAS } from '@/components/ui/FormulaReveal';
import { useDashboardFilters } from '@/contexts/DashboardFilters';
import { AmenitieBarTooltip, AmenitieRadarTooltip, PriceTooltip, ScatterTooltip } from './ChartTooltips';
import { useAIInsight } from '@/hooks/useAIInsight';
import { DashboardContext, useDashboard } from '@/contexts/DashboardContext';
import { useCompset } from '@/contexts/CompsetContext';
import { COLORS, MAX_HOTEL_NAME_CHARACTERS } from '@/constants';
import { formatCurrency } from '@/helpers/formatters';
import CustomLegend, { PriceTrendsLegend } from './ChartLegend';
import { usePlans } from '@/contexts/PlanRulesContext';
import DashboardError from '../error/DashboardError';
import { TooltipContent, TooltipProvider, TooltipTrigger, Tooltip as RadixTooltip } from '../ui/tooltip';



export default function CompetitiveAnalysis() {
  const [activeTab, setActiveTab] = useState('overview');
  const [priceChartTimeRange, setPriceChartTimeRange] = useState('7d');
  const [amenitieRadarFilter, setAmenitieRadarFilter] = useState("top");

  const { selectedCompetitors, } = useDashboardFilters();
  const { competitors, activeCompset } = useCompset();

  const { competitiveAnalysisData, marketInsightData, isLoadingMarketInsight, isLoadingCompetitiveAnalysisData, isErrorCompetitiveAnalysisData, isEnabledCompetitiveAnalysisData, allAiInsights } = useDashboard();
  const { canAccessAiInsights, calculateAiInsightRefreshCount, maxAiRefreshPerSection } = usePlans()

  // Filter competitors based on selection
  const filteredCompetitors = useMemo(() => {
    if (!competitors) return [];
    if (selectedCompetitors.length === 0) return competitors;
    return competitors.filter(c => selectedCompetitors.includes(c.competitor_id || c.id));
  }, [competitors, selectedCompetitors]);

  const ids = useMemo(() => {
    const compNames = []
    filteredCompetitors?.forEach(comp => compNames.push(comp?.name))
    return compNames
  }, [filteredCompetitors])


  const comparison = (competitiveAnalysisData?.comparison && competitiveAnalysisData?.comparison?.length > 0) ? competitiveAnalysisData?.comparison : []
  const competitorsList = filteredCompetitors.length > 0 ? filteredCompetitors : competitors || [];



  // Generate price trends data with fallback and time filter
  const getPriceTrendsData = () => {
    const daysToShow =
      priceChartTimeRange === '7d'
        ? 7
        : priceChartTimeRange === '14d'
          ? 14
          : 30;

    if (marketInsightData?.price_trends && marketInsightData?.price_trends?.length > 0) {
      const data = marketInsightData?.price_trends.slice(0, daysToShow);
      return data
    }
    return []
  };

  // AI Insight for competitive analysis - must be before any conditional returns
  const aiInsightData = useMemo(() => ({
    your_hotel: comparison.find(c => c.is_your_hotel) || {},
    competitors: comparison.filter(c => !c.is_your_hotel)
  }), [comparison]);

  const { insight: aiInsight, loading: aiLoading, refetch: refetchAI, isAIGenerated, isEnabled, enableAiInsight } = useAIInsight(
    'competitive',
    aiInsightData,
    (!isLoadingCompetitiveAnalysisData && comparison.length > 0 && isEnabledCompetitiveAnalysisData && canAccessAiInsights())
  );

  // Generate price trends data - must be before conditional returns
  const priceTrendsData = useMemo(() => {
    if (isLoadingMarketInsight) return [];
    return getPriceTrendsData();
  }, [marketInsightData, priceChartTimeRange, comparison, isLoadingMarketInsight]);


  // Generate amenities radar data - must be before conditional returns
  const radarData = useMemo(() => {
    if (isLoadingCompetitiveAnalysisData) return [];

    return competitiveAnalysisData?.amenity
  }, [competitiveAnalysisData, isLoadingCompetitiveAnalysisData]);


  const amenitiesRadarData = useMemo(() => {
    if (isLoadingCompetitiveAnalysisData || isErrorCompetitiveAnalysisData || !competitiveAnalysisData) return []

    if (amenitieRadarFilter == "top") return competitiveAnalysisData?.top5
    return competitiveAnalysisData?.least5

  }, [filteredCompetitors, amenitieRadarFilter, competitiveAnalysisData])


  if (isLoadingCompetitiveAnalysisData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading competitive analysis...</p>
        </div>
      </div>
    );
  }

  if (isErrorCompetitiveAnalysisData) {
    return <DashboardError />
  }

  // Generate scatter plot data (price vs rating)
  const scatterData = comparison.map(h => ({
    rating: h.rating,
    lowest_price: h.lowest_price,
    reviews: h.reviews,
    name: h.name,
    isYourHotel: h.is_your_hotel
  }));

  const hasAiInsightData = allAiInsights?.insights?.find(insight => insight?.section === "competitive");


  return (
    <div className="space-y-6 animate-in fade-in duration-500" data-testid="competitive-analysis">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-primary/5">
          <TabsTrigger value="overview" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-500 data-[state=active]:text-white">
            Overview
          </TabsTrigger>
          <TabsTrigger value="reviews-amenities" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-500 data-[state=active]:text-white">
            Review & Amenities Ratings
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Overview - 3 Charts */}
        <TabsContent value="overview" className="space-y-6">
          {/* KPI cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <Card className="border-2 border-emerald-500/20 bg-emerald-500/5">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Award className="h-4 w-4 text-emerald-500" />
                  Best Value
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const bestValue = comparison?.reduce((best, current) => {
                    const bestScore = (best?.rating / best?.lowest_price) * 1000;
                    const currentScore = (current?.rating / current?.lowest_price) * 1000;
                    return currentScore > bestScore ? current : best;
                  }, comparison[0] || {});

                  return (
                    <div>
                      <p className="font-semibold text-lg">{bestValue?.name?.split(',')[0]}</p>
                      <div className="flex items-center gap-2 mt-2 text-sm">
                        <Badge variant="outline" className="bg-emerald-500/10">
                          {bestValue?.rating?.toFixed(1)} ⭐
                        </Badge>
                        <span className="text-muted-foreground">{formatCurrency(bestValue?.lowest_price, activeCompset?.currency)}</span>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <Card className="border-2 border-amber-500/20 bg-amber-500/5">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-amber-500" />
                  Lowest Price
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const lowestPrice = comparison?.reduce((lowest, current) =>
                    current?.lowest_price < lowest?.lowest_price ? current : lowest
                    , comparison[0] || {});

                  return (
                    <div>
                      <p className="font-semibold text-lg">{lowestPrice?.name?.split(',')[0]}</p>
                      <div className="flex items-center gap-2 mt-2 text-sm">
                        <span className="font-bold text-amber-600">{formatCurrency(lowestPrice?.lowest_price, activeCompset?.currency)}</span>
                        <Badge variant="outline" className="bg-amber-500/10">
                          {lowestPrice?.rating?.toFixed(1)} ⭐
                        </Badge>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-500/20 bg-blue-500/5">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Star className="h-4 w-4 text-blue-500" />
                  Highest Rated
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const highestRated = comparison?.reduce((highest, current) =>
                    current?.rating > highest?.rating ? current : highest
                    , comparison[0] || {});

                  return (
                    <div>
                      <p className="font-semibold text-lg">{highestRated?.name?.split(',')[0]}</p>
                      <div className="flex items-center gap-2 mt-2 text-sm">
                        <Badge variant="outline" className="bg-blue-500/10">
                          {highestRated?.rating?.toFixed(1)} ⭐
                        </Badge>
                        <span className="text-muted-foreground">{formatCurrency(highestRated?.lowest_price, activeCompset?.currency)}</span>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
          {/* Price Comparison Table */}
          <Card className="border-2 border-primary/10 hover:shadow-xl transition-all">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    Price Comparison
                  </CardTitle>
                  <CardDescription>Current pricing across all competitors and OTA's</CardDescription>
                </div>
                <FormulaReveal
                  title="Price Comparison Table"
                  type="chart"
                  formula={`Lowest_Price = MIN(all OTA rates for hotel)
Avg_Price = AVG(all OTA rates for hotel)`}
                  description="Compares pricing data across your competitive set by aggregating rates from all OTA channels."
                  methodology={`1. Collect rates from all OTAs for each competitor
2. Calculate lowest and average rates
3. Rank by lowest available rate
4. Track OTA coverage per hotel`}
                  dataSource="OTA Rate Scraping"
                >
                  <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/50">
                    <Info className="h-3.5 w-3.5" />
                  </Button>
                </FormulaReveal>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hotel</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead className="text-right">Reviews</TableHead>
                    <TableHead className="text-center">Lowest Price</TableHead>
                    <TableHead className="text-center">
                      <TooltipProvider>
                        <RadixTooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-default">Avg Price</span>
                          </TooltipTrigger>
                          <TooltipContent
                            className="px-2 py-1 text-sm bg-black text-white rounded shadow"
                            side="top"
                            align="center"
                          >
                            <p>Average price across all distributors</p>
                          </TooltipContent>
                        </RadixTooltip>
                      </TooltipProvider>
                    </TableHead>
                    {/* <TableHead className="text-center">Avg Price</TableHead> */}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparison
                    ?.filter(comp => {
                      if (comp?.is_your_hotel) return true
                      else {
                        return ids.includes(comp?.name)
                      }
                    })
                    ?.map((item, index) => (
                      <TableRow key={index} className={item?.is_your_hotel ? 'bg-green-50' : ''}>
                        <TableCell>
                          <div className="flex gap-2">
                            <span className="font-medium">{item?.name?.length > MAX_HOTEL_NAME_CHARACTERS ? `${item?.name?.slice(0, MAX_HOTEL_NAME_CHARACTERS)}...` : item?.name}</span>
                            {item?.is_your_hotel && <Badge variant="outline" className="text-xs">You</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-center gap-1">
                            <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                            {item?.rating?.toFixed(1)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{item?.reviews?.toLocaleString()}</TableCell>
                        <TableCell className="text-center  font-medium">
                          {item?.lowest_price ? formatCurrency(item?.lowest_price, activeCompset?.currency, true) : "-"}
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground">
                          {item?.avg_price ? formatCurrency(item?.avg_price, activeCompset?.currency, true) : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Price Trends Chart - New Addition */}
          <Card className="border-2 border-primary/10 hover:shadow-xl transition-all py-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Price Trends
                  </CardTitle>
                  <CardDescription>Pricing comparison across competitive set</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Tabs value={priceChartTimeRange} onValueChange={setPriceChartTimeRange}>
                    <TabsList className="h-8">
                      <TabsTrigger value="7d" className="text-xs px-2">7D</TabsTrigger>
                      <TabsTrigger value="14d" className="text-xs px-2">14D</TabsTrigger>
                      <TabsTrigger value="30d" className="text-xs px-2">30D</TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <FormulaReveal
                    title="Competitive Price Trends"
                    type="chart"
                    formula="For each date: Lowest_Rate = MIN(all OTA rates for hotel)"
                    description="Time series showing price movements across all competitors."
                    dataSource="Historical OTA Scraping"
                  >
                    <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/50">
                      <Info className="h-3.5 w-3.5" />
                    </Button>
                  </FormulaReveal>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {priceTrendsData && priceTrendsData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={priceTrendsData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" opacity={0.3} />
                    <XAxis
                      dataKey="date"
                      interval={
                        priceChartTimeRange == "7d" ? "preserveStartEnd"
                          : "preserveStart"
                      }
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                      stroke="hsl(var(--border))"
                    />
                    <YAxis
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                      tickFormatter={(v) => formatCurrency(v, activeCompset?.currency)}
                      stroke="hsl(var(--border))"
                    />
                    <Tooltip content={<PriceTooltip
                      currency={activeCompset?.currency}
                    />} />

                    {/* Your Hotel - Prominent */}
                    <Line
                      type="monotone"
                      dataKey="yourHotel"
                      name="Your Hotel"
                      stroke="#3b82f6"
                      strokeWidth={3}
                      dot={{ fill: '#3b82f6', r: 4 }}
                      activeDot={{ r: 6 }}
                    />

                    {/* Competitors */}
                    {filteredCompetitors?.map((comp, idx) => {
                      const key = comp?.name;
                      return priceTrendsData[0]?.[key] !== undefined ? (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          name={key}
                          stroke={COLORS[idx + 1]}
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                      ) : null;
                    })}
                    {/* <Legend
                      // pay
                      content={<PriceTrendsLegend />}
                      wrapperStyle={{ paddingTop: '10px' }}
                    /> */}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[350px] text-muted-foreground">
                  No price trend data available
                </div>
              )}
            </CardContent>
            <CustomLegend
              filteredCompetitors={filteredCompetitors}
            />
          </Card>

          {/* Charts Row - Price vs Rating */}
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
            {/* Price vs Rating Scatter */}
            <Card className="border-2 border-primary/10 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-primary" />
                      Price vs Rating
                    </CardTitle>
                    <CardDescription>Value positioning analysis</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <FormulaReveal
                      title="Price vs Rating Analysis"
                      type="chart"
                      formula={`Value_Position = f(Lowest_Price, Rating, Review_Count)
Bubble_Size = normalized(Review_Count)`}
                      description="Scatter plot showing price-rating relationship to identify value positioning."
                      methodology={`1. Plot each hotel by (Rating, Price)
2. Size bubbles by review volume (proxy for popularity)
3. Identify value leaders (high rating, low price)
4. Your hotel shown as star marker`}
                      dataSource="Rate & Review Aggregation"
                    >
                      <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/50">
                        <Info className="h-3.5 w-3.5" />
                      </Button>
                    </FormulaReveal>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={320}>
                  <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" opacity={0.3} />
                    <XAxis type="number" dataKey="rating" name="Rating" domain={[1, 5]} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} stroke="hsl(var(--border))" />
                    <YAxis type="number" dataKey="lowest_price" name="Price" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} tickFormatter={(v) => formatCurrency(v, activeCompset?.currency)} stroke="hsl(var(--border))" />
                    <ZAxis type="number" dataKey="reviews" range={[100, 500]} />
                    <Tooltip content={<ScatterTooltip currency={activeCompset?.currency} />} />
                    <Scatter
                      name="Competitors"
                      data={
                        scatterData
                          ?.filter(d => !d?.isYourHotel)
                          ?.filter(d => ids.includes(d?.name))
                      }
                      fill="#94a3b8" />
                    <Scatter
                      name="Your Hotel"
                      data={scatterData?.filter(d => d?.isYourHotel)}
                      fill="hsl(var(--primary))" shape="star" />
                  </ScatterChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* AI Insight - Modern gradient card */}
          {
            canAccessAiInsights() && (
              <Card className="relative overflow-hidden border-2 border-primary/20 hover:shadow-2xl transition-all" data-testid="ai-price-analysis-card">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5" />
                <CardContent className="relative p-4 sm:p-6">

                  {/* ── Header ──────────────────────────────────────────────────────────────
        Mobile:  2 rows
          Row 1: [Icon + Title + Badge]
          Row 2:             justify-end → [Refresh/Generate] [x/x left] [ℹ]
        Desktop: 1 row
          [Icon + Title + Badge] ─── flex-1 ─── [Refresh/Generate] [x/x left] [ℹ]
    ─────────────────────────────────────────────────────────────────────────── */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">

                    {/* Left: Icon + Title + Badge */}
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="p-2.5 sm:p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg shrink-0">
                        {aiLoading ? (
                          <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 text-white animate-spin" />
                        ) : (
                          <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <h3 className="font-semibold text-foreground text-base sm:text-lg leading-tight">AI Price Analysis</h3>
                        {isAIGenerated && (
                          <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-600 border-purple-500/30">
                            Prowen AI
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Right: Actions — justify-end on mobile, inline on desktop */}
                    <div className="flex items-center justify-end gap-2 shrink-0">
                      {(calculateAiInsightRefreshCount("competitive") && (isEnabled || hasAiInsightData)) ?
                        (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1.5 h-8 px-2 sm:px-3 shrink-0"
                            onClick={() => {
                              enableAiInsight()
                              refetchAI("regenerate")
                            }}
                            disabled={aiLoading}
                            title="Refresh AI Analysis"
                          >
                            <RefreshCw className={`h-4 w-4 shrink-0 ${aiLoading ? 'animate-spin' : ''}`} />
                            <span className="text-xs sm:hidden">Refresh</span>
                          </Button>
                        )
                        : null
                      }
                      {(!isEnabled && !hasAiInsightData) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 h-8 shrink-0"
                          onClick={enableAiInsight}
                        >
                          <span className="text-xs">Generate AI Insights</span>
                        </Button>
                      )}
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {`${calculateAiInsightRefreshCount("competitive")} / ${maxAiRefreshPerSection} left`}
                      </span>
                      <FormulaReveal {...HOTEL_FORMULAS.COMPETITIVE_ANALYSIS_AI}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                          <Info className="h-4 w-4 text-muted-foreground hover:text-primary" />
                        </Button>
                      </FormulaReveal>
                    </div>
                  </div>

                  {/* Body */}
                  <p className="text-sm font-medium leading-relaxed whitespace-pre-wrap break-words">
                    {aiLoading
                      ? 'Analyzing your competitive position with Prowen AI...'
                      : aiInsight
                        ? aiInsight
                        : hasAiInsightData
                          ? hasAiInsightData?.insight
                          : null
                    }
                  </p>

                </CardContent>
              </Card>
            )
          }
        </TabsContent>

        {/* Tab 2: Review & Amenities Ratings */}
        <TabsContent value="reviews-amenities" className="space-y-8">
          {/* Amenities Vertical Bar Chart - Full Width with Horizontal Scroll */}
          <Card className="mt-5">
            <CardHeader className="flex justify-between flex-row">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Amenitie Breakdown - Hotel Amenities
                </CardTitle>
                <CardDescription>Compare amenity ratings across your competitive set  • Scroll horizontally to see all amenities →</CardDescription>
              </div>
              <FormulaReveal
                title="Category Breakdown"
                description="Compares amenity ratings across all hotels in your competitive set, broken down by category like rooms, service, location, and cleanliness."
              >
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Info className="h-4 w-4 text-muted-foreground hover:text-primary" />
                </Button>
              </FormulaReveal>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'hsl(var(--border)) transparent' }}>
                <div style={{ minWidth: `${Math.max(900, radarData?.length * 75)}px`, height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={radarData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 0 }}
                      barCategoryGap="10%"
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} horizontal={true} />
                      <XAxis
                        type="category"
                        dataKey="category"
                        tick={{ fill: 'hsl(var(--foreground))', fontSize: 12, fontWeight: 500 }}
                        // angle={-15}
                        textAnchor="end"
                        // height={85}
                        interval={0}
                        axisLine={{ stroke: 'hsl(var(--border))' }}
                        tickLine={{ stroke: 'hsl(var(--border))' }}
                      />
                      <YAxis
                        type="number"
                        domain={[0, 5]}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        tickCount={6}
                        axisLine={{ stroke: 'hsl(var(--border))' }}
                        label={{ value: 'Rating', angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      />
                      <Tooltip
                        content={<AmenitieBarTooltip />}
                        // cursor={{ fill: 'hsl(var(--muted))', opacity: 0.15 }}
                        cursor={{ fill: 'rgba(243, 244, 246, 0.9)' }}
                        offset={50}
                      />
                      <Bar
                        dataKey="YourHotel"
                        fill="hsl(var(--primary))"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={50}
                      // isAnimationActive={true}
                      // activeBar={{fill: "#1d4ed8"}}
                      />
                      {competitors
                        ?.filter(comp => {
                          return ids.includes(comp?.name)
                        })
                        .map((comp, idx) => {
                          const key = comp?.name;
                          return (
                            <Bar
                              key={comp?.competitor_id || idx}
                              dataKey={key}
                              fill={COLORS[idx + 1]}
                              // isAnimationActive={true}
                              opacity={0.85}
                              radius={[4, 4, 0, 0]}
                              maxBarSize={50}
                            />
                          );
                        })}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Legend with average scores */}
              <div className="mt-3 pt-3 border-t flex flex-wrap gap-4 text-sm justify-center">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'hsl(var(--primary))' }} />
                  <span className="font-medium">Your Hotel</span>
                  <span className="text-muted-foreground">
                    (Avg: {(radarData?.reduce((acc, d) => acc + (d['YourHotel'] || 0), 0) / radarData?.length).toFixed(1)})
                  </span>
                </div>
                {filteredCompetitors
                  ?.map((comp, idx) => {
                    const key = comp?.name;
                    const avg = radarData?.reduce((acc, d) => acc + (d[key] || 0), 0) / radarData?.length;
                    return (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx + 1] }} />
                        <span className="font-medium">{key}</span>
                        <span className="text-muted-foreground">(Avg: {avg.toFixed(1)})</span>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>

          {/* Amenities Radar Chart - Below */}
          <Card>
            <CardHeader>
              <div className='flex justify-between items-center'>
                <div>
                  <CardTitle className="text-lg">Amenities Comparison - Radar View</CardTitle>
                  <CardDescription>Visual overview of category ratings across competitors</CardDescription>
                </div>
                <div className='flex items-center gap-2'>
                  <Tabs value={amenitieRadarFilter} onValueChange={setAmenitieRadarFilter}>
                    <TabsList className="h-8">
                      <TabsTrigger value="top" className="text-xs px-2">Top</TabsTrigger>
                      <TabsTrigger value="least" className="text-xs px-2">Least</TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <FormulaReveal
                    title="Amenities Radar"
                    description="Visual radar chart showing how your hotel's amenity scores compare against competitors across all categories at a glance."
                  >
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Info className="h-4 w-4 text-muted-foreground hover:text-primary" />
                    </Button>
                  </FormulaReveal>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                <ResponsiveContainer width="100%" height={400} className="max-w-2xl">
                  <RadarChart data={amenitiesRadarData}>
                    <PolarGrid className="stroke-border" />
                    <PolarAngleAxis dataKey="category" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                    <PolarRadiusAxis angle={30} tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                    <Radar name="Your Hotel" dataKey="your" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.3} />
                    <Tooltip
                      content={<AmenitieRadarTooltip />}
                      cursor={false}
                    />

                    {filteredCompetitors
                      ?.filter(comp => ids.includes(comp?.name))
                      ?.map((comp, idx) => {
                        const key = comp?.name
                        return (
                          <Radar
                            key={comp?.competitor_id || idx}
                            name={key}
                            dataKey={key}
                            stroke={COLORS[idx + 1]}
                            fill={COLORS[idx + 1]}
                            fillOpacity={0.1}
                          />
                        );
                      })}
                    {/* <Legend /> */}
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <CustomLegend
                filteredCompetitors={filteredCompetitors}
              />
            </CardContent>
          </Card>

          {/* AI Insight for Amenities */}
          {/* <Card className="bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 border-primary/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-accent/10">
                  <Sparkles className="h-6 w-6 text-accent" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-foreground">AI Amenities Analysis</h3>
                    <FormulaReveal
                      title="Amenities Analysis AI"
                      type="ai-insight"
                      formula="N/A - Natural Language Generation"
                      description="AI-generated analysis of amenity scores and competitive positioning."
                      aiPrompt={`Analyze the amenity ratings for each hotel in the compset and provide recommendations.`}
                      dataSource="Review Sentiment Analysis"
                    >
                      <Button variant="ghost" size="sm" className="h-8 gap-1.5">
                        <Eye className="h-4 w-4" />
                        View Prompt
                      </Button>
                    </FormulaReveal>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    {hotel?.amenity_ratings?.length > 0 ? (
                      <>
                        Your hotel excels in <strong className="text-foreground">
                          {hotel.amenity_ratings.sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating))[0]?.category}
                          ({parseFloat(hotel.amenity_ratings.sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating))[0]?.rating).toFixed(1)}/5)
                        </strong>.
                        Focus on improving <strong className="text-foreground">
                          {hotel.amenity_ratings.sort((a, b) => parseFloat(a.rating) - parseFloat(b.rating))[0]?.category}
                        </strong> to strengthen your competitive position.
                      </>
                    ) : (
                      'Amenity data is being analyzed. Check back soon for insights.'
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card> */}
        </TabsContent>
      </Tabs>
    </div>
  );
}